import { useEffect, useMemo, useRef, useState } from "react";import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Globe2, Building2, Cpu, RefreshCw, Share2, FileText, Mic2, Download } from "lucide-react";
import { getCapsule, getCapsuleAudioUrl } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { playReadyChime } from "@/lib/chime";
import { loadPrefs } from "@/lib/storage";
import SourcesPanel from "@/components/SourcesPanel";
import useHotkeys from "@/hooks/useHotkeys";
import AudioPlayer from "@/components/AudioPlayer";
import LoadingMarquee from "@/components/LoadingMarquee";
import FeedbackWidget from "@/components/FeedbackWidget";
import { toast } from "sonner";

const SECTION_META = {
  global: { label: "Global", Icon: Globe2 },
  local: { label: "Local", Icon: Building2 },
  technology: { label: "Technology", Icon: Cpu },
};

function estimateSecondsForText(text) {
  const words = (text || "").trim().split(/\s+/).filter(Boolean).length;
  return words * 0.4;
}

function formatStoryDate(iso) {
  if (!iso || typeof iso !== "string") return "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  try {
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

function buildTimeline(intro, segments, outro) {
  const items = [];
  let t = 0;
  if (intro) {
    const d = estimateSecondsForText(intro);
    items.push({ kind: "intro", start: t, end: t + d });
    t += d;
  }
  segments.forEach((s, i) => {
    const headDur = estimateSecondsForText(s.headline + ".");
    const sumDur = estimateSecondsForText(s.summary);
    items.push({ kind: "segment", index: i, start: t, end: t + headDur + sumDur });
    t += headDur + sumDur;
  });
  if (outro) {
    const d = estimateSecondsForText(outro);
    items.push({ kind: "outro", start: t, end: t + d });
    t += d;
  }
  return { items, totalEstimated: t };
}

function transcriptColor(it, currentTime, isAudio) {
  if (!isAudio) return "text-zinc-950";
  if (!it) return "text-zinc-300";
  if (currentTime >= it.start && currentTime < it.end) return "text-zinc-950";
  if (currentTime >= it.end) return "text-zinc-400";
  return "text-zinc-300";
}

function buildTranscriptTextFromCapsule(capsule) {
  if (!capsule) return "";
  const lines = [];
  const date = new Date(capsule.created_at).toLocaleDateString(undefined, {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
  lines.push(`THE DAILY CAPSULE — ${date}`);
  lines.push("=".repeat(60));
  if (capsule.intro) { lines.push("", capsule.intro); }
  (capsule.segments || []).forEach((s, i) => {
    const label = (SECTION_META[s.section]?.label || s.section).toUpperCase();
    lines.push(
      "",
      `[${String(i + 1).padStart(2, "0")}] ${label}`,
      s.headline,
      "-".repeat(s.headline.length),
      s.summary,
    );
    if (s.source_hint) lines.push(`Source: ${s.source_hint}`);
  });
  if (capsule.outro) { lines.push("", capsule.outro); }
  lines.push("", "-- Generated by The Daily Capsule --");
  return lines.join("\n");
}

export default function CapsulePlayer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [capsule, setCapsule] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [transcriptUrl, setTranscriptUrl] = useState(null);
  const [showHotkeys, setShowHotkeys] = useState(false);
  const audioRef = useRef(null);
  const pollRef = useRef(null);
  const today = useMemo(() =>
    new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }),
  []);

  // Build transcript blob URL once the capsule is ready — safe to be a hook
  // at top level and gated internally by capsule state.
  useEffect(() => {
    if (!capsule || capsule.status !== "ready") {
      setTranscriptUrl(null);
      return undefined;
    }
    const lines = [];
    const date = new Date(capsule.created_at).toLocaleDateString(undefined, {
      weekday: "long", year: "numeric", month: "long", day: "numeric",
    });
    lines.push(`THE DAILY CAPSULE — ${date}`);
    lines.push("=".repeat(60));
    if (capsule.intro) { lines.push("", capsule.intro); }
    (capsule.segments || []).forEach((s, i) => {
      const label = (SECTION_META[s.section]?.label || s.section).toUpperCase();
      lines.push(
        "",
        `[${String(i + 1).padStart(2, "0")}] ${label}`,
        s.headline,
        "-".repeat(s.headline.length),
        s.summary,
      );
      if (s.source_hint) lines.push(`Source: ${s.source_hint}`);
    });
    if (capsule.outro) { lines.push("", capsule.outro); }
    lines.push("", "-- Generated by The Daily Capsule --");
    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    setTranscriptUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [capsule]);

  useEffect(() => {
    let active = true;
    let consecutiveErrors = 0;
    let firedReadyChime = false;
    async function tick() {
      try {
        const c = await getCapsule(id);
        if (!active) return;
        consecutiveErrors = 0;
        setCapsule(c);
        if (c.status === "ready" || c.status === "error") {
          if (pollRef.current) clearInterval(pollRef.current);
          if (c.status === "ready" && !firedReadyChime) {
            firedReadyChime = true;
            const prefs = loadPrefs();
            const createdMs = c.created_at ? Date.parse(c.created_at) : 0;
            const isFresh = createdMs && (Date.now() - createdMs) < 5 * 60_000;
            if (isFresh && prefs.ready_chime_enabled !== false) {
              playReadyChime({
                speak: prefs.mode === "audio",
                message: prefs.language === "hi"
                  ? "आपका डेली कैप्सूल तैयार है।"
                  : "Your daily capsule is ready.",
              });
            }
          }
        }
      } catch (e) {
        if (!active) return;
        const status = e?.response?.status;
        if (status === 404) {
          if (pollRef.current) clearInterval(pollRef.current);
          toast.error("Capsule not found.");
          navigate("/");
          return;
        }
        consecutiveErrors += 1;
        if (consecutiveErrors >= 12) {
          if (pollRef.current) clearInterval(pollRef.current);
          toast.error("Network unstable. Please refresh.");
        }
      }
    }
    tick();
    pollRef.current = setInterval(tick, 2500);
    return () => {
      active = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [id, navigate]);

  // Keyboard shortcuts. Registered before any early returns to satisfy the
  // rules of hooks. Handlers guard against not-yet-ready capsule.
  useHotkeys(
    {
      " ": (e) => {
        if (capsule?.mode !== "audio") return;
        e.preventDefault();
        const a = audioRef.current;
        if (!a) return;
        if (a.paused) a.play(); else a.pause();
      },
      k: (e) => {
        if (capsule?.mode !== "audio") return;
        e.preventDefault();
        const a = audioRef.current;
        if (!a) return;
        if (a.paused) a.play(); else a.pause();
      },
      arrowleft: () => {
        if (capsule?.mode !== "audio") return;
        const a = audioRef.current;
        if (a) a.currentTime = Math.max(0, a.currentTime - 15);
      },
      arrowright: () => {
        if (capsule?.mode !== "audio") return;
        const a = audioRef.current;
        if (a) a.currentTime = Math.min(a.duration || 0, a.currentTime + 15);
      },
      c: () => {
        if (capsule?.status !== "ready") return;
        // Fire-and-forget copy of the transcript
        (async () => {
          const text = buildTranscriptTextFromCapsule(capsule);
          const res = await copyText(text);
          if (res.ok) toast.success("Transcript copied to clipboard");
          else toast.error("Couldn't copy transcript");
        })();
      },
      g: () => { navigate("/"); },
      "?": () => setShowHotkeys((v) => !v),
      "/": (e) => { e.preventDefault(); setShowHotkeys((v) => !v); },
      escape: () => setShowHotkeys(false),
    },
    [capsule, navigate],
  );

  if (!capsule || capsule.status === "generating_script" || capsule.status === "generating_audio") {
    return (
      <LoadingMarquee
        status={capsule?.status}
        progressPct={capsule?.progress_pct || 0}
        cutoffSeconds={capsule?.cutoff_seconds}
      />
    );
  }

  if (capsule.status === "error") {
    const msg = (capsule.error || "").trim();
    const isTimeout = /cutoff|exceeded|timeout|timed out/i.test(msg);
    const isTts = /tts|audio synthesis|synthesis failed/i.test(msg);
    const isRate = /rate|quota|429|resource_exhausted/i.test(msg);
    const friendly = isRate
      ? "Gemini rate-limited us for a moment. Wait a few seconds and try again."
      : isTimeout
      ? "Generation took longer than the allowed window. Please try again — subsequent runs are usually fast."
      : isTts
      ? "Audio synthesis hit a snag. Try again, or switch to Text mode which is far more reliable."
      : msg || "Something went wrong while brewing your capsule.";

    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-zinc-50 p-8 text-center">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-rose-600">Capsule failed</p>
        <h1 className="mt-4 font-display text-4xl font-bold tracking-tight">Let's try that again</h1>
        <p data-testid="capsule-error-message" className="mt-3 max-w-xl text-sm text-zinc-600">
          {friendly}
        </p>
        {msg && msg !== friendly && (
          <details className="mt-3 max-w-xl text-xs text-zinc-400">
            <summary className="cursor-pointer">Show technical detail</summary>
            <p className="mt-2 whitespace-pre-wrap font-mono">{msg}</p>
          </details>
        )}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <button
            data-testid="retry-generate-btn"
            onClick={() => navigate("/")}
            className="bg-rose-600 px-6 py-3 text-xs uppercase tracking-[0.2em] text-white hover:bg-zinc-950"
          >
            Try again
          </button>
          <button
            data-testid="back-home-btn"
            onClick={() => navigate("/")}
            className="border border-zinc-950 px-6 py-3 text-xs uppercase tracking-[0.2em] text-zinc-950 hover:bg-zinc-950 hover:text-white"
          >
            Back to start
          </button>
        </div>
      </div>
    );
  }

  const isAudio = capsule.mode === "audio";
  const segments = capsule.segments || [];
  const { items, totalEstimated } = buildTimeline(capsule.intro, segments, capsule.outro);
  const scale = isAudio && audioDuration > 0 && totalEstimated > 0 ? audioDuration / totalEstimated : 1;
  const scaledItems = items.map((it) => ({ ...it, start: it.start * scale, end: it.end * scale }));

  const locStr = capsule.location
    ? [capsule.location.city, capsule.location.region, capsule.location.country].filter(Boolean).join(", ")
    : "";

  const onShare = async () => {
    const res = await copyText(window.location.href);
    if (res.ok) toast.success("Link copied to clipboard");
    else toast.error("Couldn't copy — try selecting the address bar and copying manually.");
  };

  const buildTranscriptText = () => buildTranscriptTextFromCapsule(capsule);
  const dateSlug = new Date(capsule.created_at).toISOString().slice(0, 10);

  const onCopyTranscript = async () => {
    const res = await copyText(buildTranscriptText());
    if (res.ok) toast.success("Transcript copied to clipboard");
    else toast.error("Couldn't copy transcript. Select the text below and use Cmd/Ctrl+C.");
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="border-b border-zinc-950 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
          <button
            data-testid="back-to-onboarding"
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-600 hover:text-zinc-950"
          >
            <ArrowLeft className="h-4 w-4" />
            New capsule
          </button>
          <div className="flex items-center gap-3">
            <span className="block h-2.5 w-2.5 animate-pulse bg-rose-600" />
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-600">
              On Air · {today}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="copy-transcript-btn"
              onClick={onCopyTranscript}
              className="inline-flex items-center gap-2 border border-zinc-300 px-3 py-2 text-[11px] uppercase tracking-[0.2em] text-zinc-700 hover:border-zinc-950 hover:text-zinc-950"
            >
              <FileText className="h-3.5 w-3.5" /> Copy
            </button>
            {transcriptUrl && (
              <a
                data-testid="download-transcript-btn"
                href={transcriptUrl}
                download={`daily-capsule-${dateSlug}.txt`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-zinc-300 px-3 py-2 text-[11px] uppercase tracking-[0.2em] text-zinc-700 hover:border-zinc-950 hover:text-zinc-950"
              >
                <Download className="h-3.5 w-3.5" /> .txt
              </a>
            )}
            {isAudio && capsule.audio_ready && (
              <a
                data-testid="download-audio-btn"
                href={getCapsuleAudioUrl(capsule.id)}
                download={`daily-capsule-${dateSlug}.wav`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-zinc-300 px-3 py-2 text-[11px] uppercase tracking-[0.2em] text-zinc-700 hover:border-zinc-950 hover:text-zinc-950"
              >
                <Download className="h-3.5 w-3.5" /> .wav
              </a>
            )}
            <button
              data-testid="share-btn"
              onClick={onShare}
              className="inline-flex items-center gap-2 border border-zinc-300 px-3 py-2 text-[11px] uppercase tracking-[0.2em] text-zinc-700 hover:border-zinc-950 hover:text-zinc-950"
            >
              <Share2 className="h-3.5 w-3.5" /> Share
            </button>
            <button
              data-testid="regenerate-btn"
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 bg-zinc-950 px-3 py-2 text-[11px] uppercase tracking-[0.2em] text-white hover:bg-rose-600"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Regenerate
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10 md:px-10">
        <div className="grid grid-cols-1 gap-8 border-b border-zinc-200 pb-10 md:grid-cols-12">
          <div className="md:col-span-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-rose-600">
              Today's {capsule.duration_minutes}-minute {isAudio ? "audio" : "text"} capsule
            </p>
            <h1 className="mt-3 font-display text-4xl font-black leading-[1] tracking-tight md:text-6xl">
              {segments[0]?.headline || "Your Daily Capsule"}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-zinc-500">
              <span className="inline-flex items-center gap-1.5 border border-zinc-950 bg-zinc-950 px-2.5 py-1 text-white" data-testid="meta-mode">
                {isAudio ? <Mic2 className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
                {isAudio ? "Audio" : "Text"}
              </span>
              {capsule.include_global && (
                <span className="border border-zinc-300 px-2.5 py-1" data-testid="meta-global">Global</span>
              )}
              {capsule.include_local && locStr && (
                <span className="border border-zinc-300 px-2.5 py-1" data-testid="meta-local">{locStr}</span>
              )}
              {capsule.include_tech && capsule.tech_topics?.length > 0 && (
                <span className="border border-zinc-300 px-2.5 py-1" data-testid="meta-tech">
                  Tech · {capsule.tech_topics.join(" · ")}
                </span>
              )}
              {isAudio && <span className="border border-zinc-300 px-2.5 py-1">Voice · {capsule.voice}</span>}
            </div>
          </div>
          <div className="md:col-span-4">
            {isAudio ? (
              <AudioPlayer
                src={getCapsuleAudioUrl(capsule.id)}
                onTime={(t, d) => { setCurrentTime(t); if (d) setAudioDuration(d); }}
                audioRef={audioRef}
              />
            ) : (
              <div className="border border-zinc-200 bg-white p-6">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">Reading time</p>
                <p className="mt-2 font-display text-4xl font-black tracking-tight text-zinc-950">
                  ~{capsule.duration_minutes} min
                </p>
                <p className="mt-3 text-sm text-zinc-500">
                  {segments.length} {segments.length === 1 ? "story" : "stories"} · scroll to read below.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-10 py-12 md:grid-cols-12">
          <section className="md:col-span-8">
            <h2 className="mb-6 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">
              {isAudio ? "Live transcript" : "Today's capsule"}
            </h2>
            <div className="space-y-10">
              {capsule.intro && (
                <p
                  data-testid="transcript-intro"
                  className={`font-display text-2xl font-medium leading-snug transition-colors duration-300 lg:text-3xl ${transcriptColor(scaledItems[0], currentTime, isAudio)}`}
                >
                  {capsule.intro}
                </p>
              )}

              {segments.map((s, i) => {
                const itemIdx = (capsule.intro ? 1 : 0) + i;
                const it = scaledItems[itemIdx];
                const color = transcriptColor(it, currentTime, isAudio);
                const Icon = SECTION_META[s.section]?.Icon || Globe2;
                return (
                  <div key={`seg-${i}-${s.headline}`} data-testid={`transcript-segment-${i}`} className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Icon className="h-4 w-4 text-rose-600" />
                      <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">
                        {SECTION_META[s.section]?.label || s.section} · Story {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className={`font-display text-3xl font-bold leading-tight transition-colors duration-300 lg:text-4xl ${color}`}>
                      {s.headline}
                    </h3>
                    <p className={`text-lg leading-relaxed transition-colors duration-300 lg:text-xl ${color}`}>
                      {s.summary}
                    </p>
                    {(s.source_hint || s.story_date || s.source_url) && (
                      <p className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">
                        {s.source_hint && <span>Source · {s.source_hint}</span>}
                        {s.story_date && (
                          <span data-testid={`segment-date-${i}`}>{formatStoryDate(s.story_date)}</span>
                        )}
                        {s.source_url && (
                          <a
                            href={s.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`segment-source-link-${i}`}
                            className="inline-flex items-center gap-1 border-b border-rose-600 text-rose-600 hover:text-zinc-950 hover:border-zinc-950"
                          >
                            View original ↗
                          </a>
                        )}
                      </p>
                    )}
                  </div>
                );
              })}

              {capsule.outro && (() => {
                const idx = (capsule.intro ? 1 : 0) + segments.length;
                const it = scaledItems[idx];
                return (
                  <p
                    data-testid="transcript-outro"
                    className={`font-display text-2xl font-medium italic leading-snug transition-colors duration-300 lg:text-3xl ${transcriptColor(it, currentTime, isAudio)}`}
                  >
                    {capsule.outro}
                  </p>
                );
              })()}

              <SourcesPanel segments={segments} />
            </div>
          </section>

          <aside className="md:col-span-4">
            <div className="md:sticky md:top-8">
              <h2 className="mb-6 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">
                {isAudio ? "Queue" : "Contents"} · {segments.length} stories
              </h2>
              <ol className="border-t border-zinc-950">
                {segments.map((s, i) => {
                  const itemIdx = (capsule.intro ? 1 : 0) + i;
                  const it = scaledItems[itemIdx];
                  const isActive = isAudio && it && currentTime >= it.start && currentTime < it.end;
                  const Icon = SECTION_META[s.section]?.Icon || Globe2;
                  return (
                    <li
                      key={`queue-${i}-${s.headline}`}
                      data-testid={`queue-item-${i}`}
                      className={`flex items-start gap-3 border-b border-zinc-200 py-4 ${isActive ? "bg-zinc-950 text-white" : ""}`}
                    >
                      <span className={`font-mono text-[11px] uppercase tracking-[0.25em] pl-1 pt-1 ${isActive ? "text-rose-400" : "text-zinc-400"}`}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div className="flex-1 pr-2">
                        <div className="flex items-center gap-2">
                          <Icon className={`h-3.5 w-3.5 ${isActive ? "text-rose-400" : "text-rose-600"}`} />
                          <span className={`font-mono text-[10px] uppercase tracking-[0.25em] ${isActive ? "text-zinc-300" : "text-zinc-500"}`}>
                            {SECTION_META[s.section]?.label || s.section}
                          </span>
                        </div>
                        <p className={`mt-1.5 font-medium leading-snug ${isActive ? "text-white" : "text-zinc-900"}`}>
                          {s.headline}
                        </p>
                        {(s.source_hint || s.story_date) && (
                          <p className={`mt-1 text-xs ${isActive ? "text-zinc-300" : "text-zinc-500"}`}>
                            {s.source_hint}
                            {s.source_hint && s.story_date && " · "}
                            {s.story_date && formatStoryDate(s.story_date)}
                          </p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          </aside>
        </div>
      </main>

      <AnimatePresence>
        {capsule.status === "ready" && isAudio && !capsule.audio_ready && (
          <motion.div
            initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }}
            className="fixed bottom-6 left-1/2 z-40 -translate-x-1/2 border border-zinc-950 bg-white px-4 py-2 text-xs uppercase tracking-[0.25em] text-zinc-700"
          >
            Audio rendering…
          </motion.div>
        )}
      </AnimatePresence>

      {/* Discreet keyboard hint — offset above the Feedback button so they stack */}
      <button
        type="button"
        data-testid="hotkeys-hint"
        onClick={() => setShowHotkeys(true)}
        className="fixed bottom-16 left-4 z-30 hidden items-center gap-2 border border-zinc-300 bg-white/90 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600 backdrop-blur hover:border-zinc-950 hover:text-zinc-950 md:inline-flex"
        aria-label="Show keyboard shortcuts"
      >
        <kbd className="border border-zinc-300 px-1 text-[10px]">?</kbd>
        Shortcuts
      </button>

      <AnimatePresence>
        {showHotkeys && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/60 backdrop-blur-sm p-4"
            onClick={() => setShowHotkeys(false)}
            data-testid="hotkeys-modal"
          >
            <motion.div
              initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md border border-zinc-950 bg-white p-8"
              role="dialog" aria-modal="true" aria-label="Keyboard shortcuts"
            >
              <div className="flex items-baseline justify-between">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-rose-600">
                  Keyboard shortcuts
                </p>
                <button
                  type="button"
                  onClick={() => setShowHotkeys(false)}
                  className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-zinc-950"
                  aria-label="Close shortcuts"
                >
                  Esc ×
                </button>
              </div>
              <h3 className="mt-3 font-display text-3xl font-bold tracking-tight">Quick keys</h3>
              <dl className="mt-6 divide-y divide-zinc-200">
                {[
                  { keys: ["Space", "K"], label: "Play / pause audio", audioOnly: true },
                  { keys: ["←"], label: "Skip back 15 s", audioOnly: true },
                  { keys: ["→"], label: "Skip forward 15 s", audioOnly: true },
                  { keys: ["C"], label: "Copy transcript" },
                  { keys: ["G"], label: "Regenerate capsule" },
                  { keys: ["?"], label: "Show/hide this help" },
                ].map((row) => (
                  <div key={row.label} className="flex items-center justify-between py-3">
                    <span className={`text-sm ${row.audioOnly && !isAudio ? "text-zinc-400" : "text-zinc-800"}`}>
                      {row.label}
                      {row.audioOnly && !isAudio && (
                        <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">audio only</span>
                      )}
                    </span>
                    <span className="flex gap-1.5">
                      {row.keys.map((k) => (
                        <kbd key={k} className="border border-zinc-300 bg-zinc-50 px-2 py-0.5 font-mono text-xs text-zinc-800">
                          {k}
                        </kbd>
                      ))}
                    </span>
                  </div>
                ))}
              </dl>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <FeedbackWidget context="capsule" capsuleId={capsule.id} />
    </div>
  );
}
